import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import _ from 'lodash'
import './HomeViewStaff.scss'
import axios from 'axios'
import moment from 'moment'
import Select from 'react-select'
import constants from '../../../lib/constants'
import { isDeviceActive, isDeviceLensShaded, resolveMACAddress } from '../../../lib/helpers'
import queryString from 'query-string'
import { Grid, _ as grid_react } from 'gridjs-react'
import 'gridjs/dist/theme/mermaid.css'
import semver from 'semver'

const DERIVED_MAC_HINT = 'Computed from the board serial, not reported by the ' +
  'device. Do not give this to a customer network team.'

const renderMAC = (device, wifi) => {
  const { mac, derived } = resolveMACAddress(device, wifi)
  if (!mac) return <span className='text-muted'>unknown</span>
  return (
    <span className='text-monospace'>{mac}
      {derived && <em className='text-muted' title={DERIVED_MAC_HINT}> (derived)</em>}
    </span>
  )
}

class HomeViewStaff extends React.Component {
  static defaultProps = {
    deviceHistory: [],
    deviceDeploymentState: constants.DEPLOYMENT_STATES.PRODUCTION
  }

  static propTypes = {
    deviceHistory: PropTypes.shape({}),
    deviceDeploymentState: PropTypes.string,
    loadDeviceHistory: PropTypes.func,
    promoteDevice: PropTypes.func,
    setDeviceDeploymentState: PropTypes.func,
    startLensCalibration: PropTypes.func,
    requestLogUpload: PropTypes.func,
    requestOnDeviceQCTest: PropTypes.func,
    calibrateStage: PropTypes.func,
    requestSpeedTest: PropTypes.func,
    restartDevice: PropTypes.func,
    revokeToken: PropTypes.func,
  }

  constructor (props) {
    super(props)
    this.state = {
      jobId: null,
      jobIds: null,
      showAlert: false,
      promotingDevice: null,
      nextDeploymentState: null,
      selectedDeviceOptions: []
    }
  }

  componentDidMount () {
    const { loadDeviceHistory, deviceDeploymentState } = this.props
    loadDeviceHistory(deviceDeploymentState)

    if (this.props.location.hash){
      this.props.setDeviceDeploymentState(this.props.location.hash.substr(1))
      this.props.loadDeviceHistory(this.props.location.hash.substr(1))
    }
  }

  componentWillReceiveProps (newProps) {
    const { location } = this.props
    if (this.props.deviceHistory !== newProps.deviceHistory) {
      let params = queryString.parse(location.search)
      if (params.devices) {
        const deviceStrings = params.devices.split(',')
        const selectedDeviceOptions = _.compact(_.map(deviceStrings, (deviceString) => {
          const d = newProps.deviceHistory[deviceString]
          if (d) {
            return { label: `${d.device_string} - ${d.data.name}`, value: d }
          }
        }))
        this.setState({
          selectedDeviceOptions
        })
      }
    }
  }

  handleDeviceSelect = (options) => {
    const { router } = this.props
    router.push({
      pathname: '/',
      search: '?' + new URLSearchParams({ devices: _.map(options, (o) => o.value.device_string) }).toString(),
      hash: '#' + this.props.deviceDeploymentState
    })
    this.setState({
      selectedDeviceOptions: options
    })
  }

  startTest = (deviceString, stripType, numRuns = 1) => {
    if (deviceString) {
      if (numRuns === 1) {
        axios.post(`${constants.API_URL}/devices/${deviceString}/start`, {
          strip_type: stripType
        })
        .then((response) => {
          this.setState({
            jobId: response.data.job_id,
            deviceString,
            showAlert: true
          })
        })
      } else {
        axios.post(`${constants.API_URL}/devices/${deviceString}/start_many`, {
          strip_type: stripType,
          num_runs: numRuns
        })
        .then((response) => {
          this.setState({
            jobIds: response.data.job_ids,
            deviceString,
            showAlert: true
          })
        })
      }
    }
  }

  startQCTest = (deviceString, qcid, stripType) => {
    if (deviceString) {
      axios.get(`${constants.API_URL}/devices/${deviceString}/start`, {
        params: {
          strip_type: stripType
        }
      })
      .then((response) => {
        if (this.qcJobList == null) {
          this.qcJobList = qcid + '_' + response.data.job_id
        } else {
          this.qcJobList = this.qcJobList + ' ' + qcid + '_' + response.data.job_id
        }
        this.setState({
          jobId: response.data.job_id,
          deviceString,
          showAlert: true
        })
      })
    }
  }

  startLifecycleTest = (deviceString) => {
    if (deviceString) {
      axios.post(`${constants.API_URL}/devices/${deviceString}/run_lifecycle_test`, {}, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(() => {
        alert('Lifecycle test started successfully')
      })
      .catch((error) => {
        console.error('Error starting lifecycle test:', error)
        alert('Failed to start lifecycle test')
      })
    }
  }

  hideAlert = () => {
    this.setState({
      showAlert: false
    })
  }

  getMetricStyle = (metric, goodThreshold, okThreshold) => {
    if (metric > goodThreshold) {
      return 'good-metric-value'
    } else if (metric > okThreshold) {
      return 'ok-metric-value'
    } else {
      return 'poor-metric-value'
    }
  }

  setPromotingDevice = (deviceString, deploymentState) => {
    this.setState({
      promotingDevice: deviceString,
      nextDeploymentState: deploymentState
    })
  }

  setDeviceName = (deviceString, name) => {
    if (deviceString) {
      axios.post(`${constants.API_URL}/devices/${deviceString}/set_device_name`, {
        name: name
      })
    }
  }

  areTestsPassed = (device) => {
    return _.every(constants.REQUIRED_TESTS, (testGroup) => {
      return _.every(testGroup.tests, (test) => {
        return _.get(device, ['data', 'unit_test_results', testGroup.key, test], false)
      })
    })
  }

  promoteDevice = (device, nextDeploymentState) => {
    const { promoteDevice } = this.props
    promoteDevice(device, nextDeploymentState)
    .then(() => {
      this.setState({
        showAlert: false
      })
    })
  }

  switchDeviceList = (deploymentState) => {
    const { loadDeviceHistory, setDeviceDeploymentState } = this.props
    setDeviceDeploymentState(deploymentState)
    loadDeviceHistory(deploymentState)
    window.history.pushState(deploymentState, deploymentState, `#${deploymentState}`);
  }

  renderNetworkInfo (device) {
    let networkInfo = [
      <div>{device.ip}</div>
    ]

    if (device.network.info) {
      networkInfo.push(
        <div>{device.network.info.is_wired ? 'Wired' : 'WiFi'}</div>
      )
      if (device.network.info.ssid) {
        let info = device.network.info
        networkInfo.push(
          <div>SSID: {info.ssid}</div>
        )
        networkInfo.push(
          <div className={`${this.getMetricStyle(info.signal, -50, -60)}`}>Signal: {120 + info.signal}/90</div>
        )
      }
    }
    return (<div style={{ 'overflowX': 'scroll' }}>{networkInfo}</div>)
  }

  render () {
    const { deviceHistory, deviceDeploymentState, startLensCalibration, requestLogUpload, requestOnDeviceQCTest, requestSpeedTest,
      revokeToken, restartDevice, calibrateStage } = this.props
    const { jobId, jobIds, deviceString, showAlert, promotingDevice, nextDeploymentState,
      selectedDeviceOptions } = this.state
    const filteredDeviceHistory = _.filter(deviceHistory, (device) => {
      return device.data.deployment_state === deviceDeploymentState
    })
    const sortedDevices = _.sortBy(filteredDeviceHistory, (device) => {
      if (device.last_active) {
        return 0 - new Date(device.last_active).getTime()
      }
    })
    const deviceOptions = _.map(sortedDevices, (d) => ({ label: `${d.device_string} - ${d.data.name}`, value: d }))
    let renderedDevices = null
    if (selectedDeviceOptions.length > 0) {
      renderedDevices = _.map(selectedDeviceOptions, (option) => option.value)
    } else {
      renderedDevices = sortedDevices
    }

    const columns = [
      {
        name: 'Device ID',
        width: '200px',
        formatter: (device) => grid_react(
          <div>
            <a href={`/device/${device.device_string}`}>{device.device_string}</a>

            <input
              onKeyPress={(ev) => {
                if (ev.key === 'Enter') {
                  alert('Device name updated to: ' + ev.target.value)
                  this.setDeviceName(device.device_string, ev.target.value)
                  console.log(this)
                  ev.preventDefault()
                }
              }}
              type='text'
              name='name'
              defaultValue={device.data.name} />

            <div>WiFi MAC addr: {renderMAC(device, true)}</div>

            <div>Ethernet MAC addr: {renderMAC(device, false)}</div>

          </div>
        )
      },
      {
        name: 'S/W Version',
        width: '115px',
        formatter: (device) => grid_react(
          <div>
            {(device.data.is_software_updating ? 'Updating from ' : '') + (device.software_version || '') }
            <br />
            Target: {device.software_version_target}
          </div>
        ),
        sort: {
          enabled: true,
          compare: (a, b) => {
            if (a.software_version && b.software_version) {
              return semver.compare(a.software_version, b.software_version)
            } else if (a.software_version) {
              return 1
            } else if (b.software_version) {
              return -1
            } else {
              return 0
            }
          }
        }
      },
      {
        name: 'Test Status',
        width: '85px',
        formatter: (cell) => {
          return grid_react(
            <div className={cell ? 'table-success' : 'table-danger'}>
              {cell ? 'PASSED' : 'FAILED'}
            </div>
          )
        }
      },
      {
        name: 'Strip Present',
        width: '85px'
      },
      {
        name: 'Users',
        width: '150px',
        formatter: (users) => {
          return grid_react(
            <div style={{ 'overflowX': 'scroll' }}>
              {
                _.map(users, (u) => (<div key={u}>{u}</div>))
              }
            </div>
          )
        }
      },
      {
        name: 'Last Used',
        width: '95px',
        formatter: (lastUsed) => {
          return new Date(lastUsed).toLocaleString()
        },
        sort: {
          enabled: true,
          compare: (a, b) => {
            a = new Date(a)
            b = new Date(b)

            return (
              isFinite(a) && isFinite(b) ? (a > b) - (a < b) : NaN
            )
          }
        }
      },
      {
        name: 'Network',
        width: '100px'
      },
      {
        name: 'Speedtest',
        width: '150px',
        formatter: (speedTest) => {
          if (speedTest) {
            return grid_react(
              <div>
                <div>{moment.utc(speedTest.timestamp).local().format('MMMM Do YYYY, h:mm:ss a')}</div>
                <div className={`${this.getMetricStyle(speedTest.download, 2000000, 1500000)}`}>
                  Down: {(speedTest.download / 1000000).toFixed(2)} Mbps
                </div>
                <div className={`${this.getMetricStyle(speedTest.upload, 1500000, 1000000)}`}>
                  Up: {(speedTest.upload / 1000000).toFixed(2)} Mbps
                </div>
              </div>
            )
          }
        }
      },
      {
        name: 'Test',
        width: '60px',
        formatter: (deviceString) => grid_react(
          <a href={`/test/${deviceString}`}>
            <button type='button' className='btn btn-outline-primary clickable'>
              Test
            </button>
          </a>
        )
      },
      {
        name: 'PCB',
        width: '60px',
        formatter: (deviceString) => grid_react(
          <a href={`/pcb/${deviceString}`}>
            <button type='button' className='btn btn-outline-secondary clickable'>
              PCB
            </button>
          </a>
        )
      },
      {
        name: 'Lens',
        width: '100px',
        formatter: (device) => {
          const isLensShaded = isDeviceLensShaded(device)

          return grid_react(
            <button
              type='button'
              className={`btn ${isLensShaded ? 'btn-success' : 'btn-outline-success'} clickable`}
              onClick={() => startLensCalibration(device.device_string)}
            >
              { isLensShaded ? ' Recalibrate Lens' : 'Calibrate Lens' }
            </button>
          )
        }
      },
      {
        name: 'Promote',
        width: '100px',
        formatter: (device) => grid_react(
          <div className='btn-group'>
            <button
              type='button'
              className='btn btn-outline-success clickable'
              data-toggle='modal'
              data-target='#confirmModal'
              onClick={() => this.setPromotingDevice(device, constants.DEPLOYMENT_STATES.PRODUCTION)}
            >
              Promote
            </button>
            <button type='button' className='btn btn-outline-info dropdown-toggle dropdown-toggle-split'
              data-toggle='dropdown' aria-haspopup='true' aria-expanded='false'>
              <span className='sr-only'>
                Toggle Dropdown
              </span>
            </button>
            <div className='dropdown-menu'>
              <div
                className='dropdown-item'
                data-toggle='modal'
                data-target='#confirmModal'
                onClick={() => this.setPromotingDevice(device, constants.DEPLOYMENT_STATES.STAGING)}
                >
                Staging
              </div>
              <div
                className='dropdown-item'
                data-toggle='modal'
                data-target='#confirmModal'
                onClick={() => this.setPromotingDevice(device, constants.DEPLOYMENT_STATES.MANUFACTURING)}
                >
                Manufacturing
              </div>
              <div
                className='dropdown-item'
                data-toggle='modal'
                data-target='#confirmModal'
                onClick={() => this.setPromotingDevice(device, constants.DEPLOYMENT_STATES.RESEARCH)}
                >
                Research
              </div>
            </div>
          </div>
        )
      },
      {
        name: 'Run',
        width: '100px',
        formatter: (device) => grid_react(
          <div className='btn-group'>
            <button
              type='button'
              className={`btn ${isDeviceActive(device) ? 'btn-info' : 'btn-outline-info'} clickable`}
              onClick={() => this.startTest(device.device_string, 'wbc_4x')}
            >
              Run
            </button>
            <button type='button' className='btn btn-outline-info dropdown-toggle dropdown-toggle-split'
              data-toggle='dropdown' aria-haspopup='true' aria-expanded='false'>
              <span className='sr-only'>
                Toggle Dropdown
              </span>
            </button>
            <div className='dropdown-menu dropdown-actions'>
              <a href={`/qc/${device.device_string}/create`}>
                <div
                  className='dropdown-item'
                  >
                  Lot Verification QC
                </div>
              </a>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.WBC_CONTROL)}
              >
                Control
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, 'wbc_4x', 10)}
              >
                Run 10 Tests
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, 'platelet', 10)}
              >
                Run 10 Platelet Tests
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, 'hemoglobin_green')}
              >
                HB Green
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, 'hemoglobin')}
              >
                HB
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, 'platelet')}
              >
                Platelet
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, 'platelet_qc')}
              >
                Platelet QC
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.PLATELET_CONTROL_FLUID)}
              >
                Platelet Control
              </div>
                { device.data.deployment_state === constants.DEPLOYMENT_STATES.MANUFACTURING ?
                  <div
                    className='dropdown-item'
                    onClick={() => revokeToken(device.device_string)}
                  >
                    Revoke Token
                  </div>
               : null }
              <div
                className='dropdown-item'
                onClick={() => requestLogUpload(device.device_string)}
              >
                Request Logs
              </div>
              <div
                className='dropdown-item'
                onClick={() => calibrateStage(device.device_string)}
              >
                Find Stage Corners
              </div>
              <div
                className='dropdown-item'
                onClick={() => requestSpeedTest(device.device_string)}
              >
                Request Speed Test
              </div>
              <div
                className='dropdown-item'
                onClick={() => restartDevice(device.device_string)}
              >
                Restart Device
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startLifecycleTest(device.device_string)}
              >
                Run Lifecycle Test
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.WBC_COUNTROL_AUTO_CHECK_LOW)}
              >
                Auto-Check Low
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.WBC_COUNTROL_AUTO_CHECK_NORMAL)}
              >
                Auto-Check Normal
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.WBC_COUNTROL_AUTO_CHECK_HIGH)}
              >
                Auto-Check High
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.COMBINED_AUTO_CHECK_LOW)}
              >
                Combined AC Low
              </div>
              <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.COMBINED_AUTO_CHECK_NORMAL)}
              >
                Combined AC Normal
              </div>
                            <div
                className='dropdown-item'
                onClick={() => this.startTest(device.device_string, constants.STRIP_TYPES.COMBINED_AUTO_CHECK_HIGH)}
              >
                Combined AC High
              </div>
              <div
                className='dropdown-item'
                onClick={() => requestOnDeviceQCTest(device.device_string)}
              >
                On-Device QC Test
              </div>
            </div>
          </div>
        )
      }
    ]

    const data = _.map(renderedDevices, (device) =>
      [
        device,
        device,
        this.areTestsPassed(device),
        String(device.is_strip_present),
        device.users,
        device.last_active,
        grid_react(this.renderNetworkInfo(device)),
        device.network.speed_test,
        device.device_string,
        device.device_string,
        device,
        device,
        device
      ]
    )

    const gridStyles = {
      container: {
      },
      table: {
        border: 'none',
        'border-collapse': 'separate',
        'border-spacing': 0

      },
      td: {
        border: 'none',
        'border-bottom': '1px solid #c3c3c3',
        'text-align': 'center '
      },
      th: {
        border: 'none',
        'background-color': '#c3c3c3',
        'text-align': 'center',
        'padding': '7px',
        'overflow': 'wrap'
      },
      header: {

      },
      footer: {
        'box-shadow': 'none '
      }
    }

    return (
      <div>
        {
          showAlert
            ? <div className='alert alert-primary' role='alert'>
              Job {jobIds || jobId} started on device {deviceString}
              <button onClick={this.hideAlert} type='button' className='close clickable' aria-label='Close'>
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
            : null
        }
        <ul className='nav nav-tabs'>
          <li className='nav-item'>
            <div
              className={`nav-link ${deviceDeploymentState === constants.DEPLOYMENT_STATES.PRODUCTION ? 'active' : ''}`}
              onClick={() => this.switchDeviceList(constants.DEPLOYMENT_STATES.PRODUCTION)}>
              Production
            </div>
          </li>
          <li className='nav-item'>
            <div
              className={`nav-link ${deviceDeploymentState === constants.DEPLOYMENT_STATES.STAGING ? 'active' : ''}`}
              onClick={() => this.switchDeviceList(constants.DEPLOYMENT_STATES.STAGING)}>
              Staging
            </div>
          </li>
          <li className='nav-item'>
            <div
              className={`nav-link ${deviceDeploymentState === constants.DEPLOYMENT_STATES.MANUFACTURING ? 'active' : ''}`}
              onClick={() => this.switchDeviceList(constants.DEPLOYMENT_STATES.MANUFACTURING)}>
              Manufacturing
            </div>
          </li>
          <li className='nav-item'>
            <div
              className={`nav-link ${deviceDeploymentState === constants.DEPLOYMENT_STATES.RESEARCH ? 'active' : ''}`}
              onClick={() => this.switchDeviceList(constants.DEPLOYMENT_STATES.RESEARCH)}>
              Research
            </div>
          </li>
        </ul>

        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <Select
            options={deviceOptions}
            value={selectedDeviceOptions}
            onChange={this.handleDeviceSelect}
            multi
          />
        </div>
        <Grid
          data={data}
          columns={columns}
          search={{
            selector: (cell, rowIndex, cellIndex) => {
              if (cellIndex === 0) {
                return cell.data.name
              } else if (cellIndex === 1) {
                return cell.software_version
              } else {
                return cell
              }
            }
          }}
          style={gridStyles}
          pagination={{
            enabled: true,
          }}
        />
        <div className='modal' id='confirmModal' tabIndex='-1' role='dialog'>
          <div className='modal-dialog' role='document'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Confirm Promoting/Demoting</h5>
                <button type='button' className='close' data-dismiss='modal' aria-label='Close'>
                  <span aria-hidden='true'>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <p>Are you sure you want to promote/demote device {promotingDevice ? promotingDevice.device_string : ''} ?</p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-primary'
                  data-dismiss='modal'
                  onClick={() => this.promoteDevice(promotingDevice, nextDeploymentState)}
                >
                  Yes
                </button>
                <button type='button' className='btn btn-secondary' data-dismiss='modal'>Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default HomeViewStaff



// WEBPACK FOOTER //
// ./src/routes/Home/components/HomeViewStaff.js